import { useMemo } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { ProgressSpinner } from '@tools/components/commonFields.tsx';
import { useAppSelector } from '@store/store.tsx';
import {
  selectErpEnabled,
  selectPlanCapabilities,
  selectSiteApps,
  selectSiteFeatureFlags,
  selectSiteInfoStatus,
} from '@store/slices/settingsSlice.tsx';
import {
  selectIsSuperuser,
  selectUserGroups,
  selectUserPermissions,
  selectUserRoles,
} from '@store/slices/userSlice.tsx';
import { isErpPermission } from '@routes/pageRoutes.ts';
import { evaluateRouteAccess, findRouteAccessMeta } from './routeAccess.ts';

import { useCmsAccess } from '../hooks/useCmsAccess.ts';

interface RequireStaffProps {
  children?: ReactNode;
  render?: () => ReactElement;
  /** Route to use when access is denied after authentication/staff checks. */
  fallbackPath?: string;
  requiredApps?: readonly string[];
  requiredAnyApps?: readonly string[];
  requiredCapabilities?: readonly string[];
  requiredGroups?: readonly string[];
  requiredPermissions?: readonly string[];
  requiresStaff?: boolean;
}

export const RequireStaff = ({
  children,
  render,
  fallbackPath = '/dashboard',
  requiredApps: explicitRequiredApps,
  requiredAnyApps: explicitRequiredAnyApps,
  requiredCapabilities: explicitRequiredCapabilities,
  requiredGroups: explicitRequiredGroups,
  requiredPermissions: explicitRequiredPermissions,
  requiresStaff: explicitRequiresStaff,
}: RequireStaffProps) => {
  const cmsAccess = useCmsAccess();
  const location = useLocation();
  const erpEnabled = useAppSelector(selectErpEnabled);
  const siteApps = useAppSelector(selectSiteApps) ?? [];
  const siteFeatures = useAppSelector(selectSiteFeatureFlags);
  const planCapabilities = useAppSelector(selectPlanCapabilities);
  const siteInfoStatus = useAppSelector(selectSiteInfoStatus);
  const permissions = useAppSelector(selectUserPermissions) ?? [];
  const groups = useAppSelector(selectUserGroups) ?? [];
  const roles = useAppSelector(selectUserRoles) ?? [];
  const isSuperuser = useAppSelector(selectIsSuperuser);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  const routeAccess = useMemo(
    () => findRouteAccessMeta(location.pathname, location.search),
    [location.pathname, location.search],
  );

  const requiresStaff = explicitRequiresStaff ?? routeAccess?.requiresStaff ?? true;
  const requiredApps = explicitRequiredApps ?? routeAccess?.requiredApps ?? [];
  const requiredAnyApps = explicitRequiredAnyApps ?? routeAccess?.requiredAnyApps ?? [];
  const requiredCapabilities = explicitRequiredCapabilities ?? routeAccess?.requiredCapabilities ?? [];
  const requiredGroups = explicitRequiredGroups ?? routeAccess?.requiredGroups ?? [];
  const requiredPermissions = explicitRequiredPermissions ?? routeAccess?.requiredPermissions ?? [];
  const isCmsRoute = routeAccess?.domain === 'cms' || routeAccess?.navGroup === 'cms';
  const requiresErpScope =
    routeAccess &&
    (routeAccess.domain === 'erp' || routeAccess.domain === 'erpDashboard' || routeAccess.navGroup === 'erp');

  if (cmsAccess.status === 'loading' || siteInfoStatus === 'loading') {
    return <ProgressSpinner isLoading />;
  }

  if (requiresStaff && !cmsAccess.isStaff) {
    if (isAuthenticated) {
      return <Navigate to="/no-access" replace />;
    }
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  const accessDecision = evaluateRouteAccess(
    {
      key: routeAccess?.key,
      featureFlag: routeAccess?.featureFlag,
      requiredApps,
      requiredAnyApps,
      requiredCapabilities,
      requiredGroups,
      requiredPermissions,
      requiresStaff,
      navGroup: routeAccess?.navGroup,
      domain: routeAccess?.domain,
      allowSuperuserWithoutApp: routeAccess?.allowSuperuserWithoutApp,
    },
    {
      apps: siteApps,
      features: siteFeatures,
      planCapabilities,
      isStaff: cmsAccess.isStaff,
      isSuperuser,
      permissions,
      groups,
      roles,
      requiresDomainPermission: Boolean(requiresErpScope),
      domainPermissions: permissions.filter(isErpPermission),
    },
  );

  if (!accessDecision.allowed && accessDecision.reason !== 'missing-staff') {
    return (
      <Navigate
        to={fallbackPath}
        state={{
          from: location,
          reason: accessDecision.reason,
          missingApps: accessDecision.missingApps,
          missingCapabilities: accessDecision.missingCapabilities,
        }}
        replace
      />
    );
  }

  if (!erpEnabled && !isCmsRoute) {
    if (render) {
      return render();
    }
    return <>{children}</>;
  }

  if (isCmsRoute && !cmsAccess.canAccess) {
    const target = cmsAccess.reason === 'role' ? '/auth/login' : '/dashboard';
    return <Navigate to={target} state={{ from: location }} replace />;
  }

  if (render) {
    return render();
  }

  return <>{children}</>;
};
